import React, {useCallback, useEffect, useMemo, useState} from 'react'
import useTranslate from "../../../modules/local/useTranslate";
import {FaRegTrashCan} from "react-icons/fa6";
import {Button, Input} from "antd";
import classNames from "classnames";
import TextArea from "antd/lib/input/TextArea";

// Thang trạng thái điểm danh với status là số nguyên và tên tiếng Anh
const ATTENDANCE_STATUSES = [
    {display: 'in attendance', status: 1, requiresNote: false, color: 'rgb(22 163 74)'}, // Green-600
    {display: 'late', status: -3, requiresNote: true, color: 'rgb(202 138 4)'}, // Yellow-600
    {display: 'excused absence', status: -1, requiresNote: true, color: 'rgb(37 99 235)'}, // Blue-600
    {display: 'unexcused absence', status: -2, requiresNote: true, color: 'rgb(220 38 38)'}, // Red-600
];

/**
 * Component hiển thị trạng thái điểm danh và lý do/ghi chú.
 */
const AttendanceDisplay = ({status, absentReason, numberOfSessions, currentStatusConfig, onClear, onEdit}) => {
    const t = useTranslate();
    return (
        <div className="flex flex-col p-4 border border-color-50 shadow-md rounded-xl text-gray-800 transition duration-150">
            <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-500 uppercase">
                    {t("selected status")}
                </span>
                {status !== null && (
                    <Button
                        color="primary" variant="filled"
                        onClick={onClear}
                        className="p-2 bg-primary-50 hover:bg-primary-100 text-primary-600 shadow-md rounded-md no-border transition duration-150"
                        title={t("clear grade")}
                        icon={<FaRegTrashCan />}
                    />
                )}
            </div>

            <span className="font-extrabold text-3xl" style={{color: currentStatusConfig?.color}}>
                {currentStatusConfig ? t(currentStatusConfig.display) : '— Select Status —'}
            </span>

            <div className="mt-1 flex items-center gap-1 text-xs">
                <span className="text-gray-700">
                    {`${t("number sessions")}: `}
                </span>
                <span className="font-bold text-color-100 tracking-wide">
                    {numberOfSessions} {t('sessions')}
                </span>
            </div>

            {absentReason && currentStatusConfig?.requiresNote && (
                <div className="mt-3 p-2 background-50 border border-color rounded-lg">
                    <span className="font-medium text-sm text-gray-500 mb-2">
                        {`${t("reason")}: `}
                    </span>
                    <p className="text-sm text-gray-700 italic whitespace-pre-wrap">{absentReason}</p>
                </div>
            )}

            {status !== null && currentStatusConfig?.requiresNote && (
                <div
                    onClick={onEdit} // Force entry/re-entry to the reason stage
                    className="mt-2 pt-2 text-center text-xs font-medium text-primary-500 hover:text-primary tracking-wider cursor-pointer transition active:scale-95">
                    {`${t("edit reason")} (${t(currentStatusConfig.display)})`}
                </div>
            )}
        </div>
    )
};

/**
 * Component cho phép chọn trạng thái điểm danh.
 */
const AttendanceStatusSelector = ({status, onSelect}) => {
    const t = useTranslate();

    return (
        <div className="p-4 bg-white rounded-xl shadow-lg border border-indigo-100">
            <h3 className="font-semibold text-gray-500 mb-3">
                1. {t("Select Attendance Status")}
            </h3>
            <div className="grid grid-cols-2 gap-4">
                {ATTENDANCE_STATUSES.map((s) => {
                    const isSelected = status === s.status;
                    return (
                        <button
                            key={s.status}
                            onClick={() => onSelect(s.status)}
                            className={classNames(
                                "py-3 text-center text-sm font-semibold shadow rounded-lg transition-all duration-200 active:scale-95",
                                isSelected
                                    ? `${s.color.replace('rgb(', 'bg-[').replace(')', ']')} text-white`
                                    : 'background text-gray-700 hover:ring-2 hover:ring-indigo-200'
                            )}
                            style={isSelected ? {backgroundColor: s.color} : {}}>
                            {t(s.display)}
                        </button>
                    );
                })}
            </div>
        </div>
    )
};

/**
 * Component cho phép nhập lý do vắng mặt.
 */
const AttendanceReasonInput = ({absentReason, onReasonChange, tempStatusConfig, onBack}) => {
    const t = useTranslate();

    return (
        <div className="p-4 background rounded-xl shadow-lg border border-color">
            <h3 className="font-semibold text-gray-500 mb-3">
                2. {t("Enter reason for {status}", {status: t(tempStatusConfig?.display)})}
            </h3>
            <TextArea
                value={absentReason}
                onChange={onReasonChange}
                placeholder={
                    `${t("Enter reason for {status}",
                        {status: t(tempStatusConfig?.display)}
                    )} ... (${t("Required for saving",
                        {status: t(tempStatusConfig?.display)}
                    )})`
                }
                rows="4"
                className="w-full p-3 border border-color rounded-lg resize-none"
            />
            <div
                onClick={onBack}
                className="mt-2 pt-2 text-center text-xs font-medium text-primary-500 hover:text-primary tracking-wider cursor-pointer transition active:scale-95">
                {t("Back to Status Selection")}
            </div>
        </div>
    )
};

/**
 * Custom input component for entering attendance status with sessions and reason.
 * Behaves like a controlled input via value and onChange props.
 * value: {status: number | null, absent_reason: string, number_of_sessions: number}
 */
const AttendanceInput = ({defaultValues, onValuesChange}) => {
    const t = useTranslate();
    // Destructure value with new names and defaults
    const {status, absent_reason, number_of_sessions} = defaultValues || {
        status: null,
        absent_reason: '',
        number_of_sessions: 1
    };

    // tempStatus controls the view stage (status selection vs reason input)
    // We store the status in temp state to allow toggling back to status selector
    const [tempStatus, setTempStatus] = useState(status);

    useEffect(() => {
        setTempStatus(status);
    }, [status]);

    const currentStatusConfig = useMemo(() => ATTENDANCE_STATUSES.find(s => s.status === status), [status]);
    const tempStatusConfig = useMemo(() => ATTENDANCE_STATUSES.find(s => s.status === tempStatus), [tempStatus]);

    const handleSessionsChange = useCallback((e) => {
        const num = parseInt(e.target.value, 10);
        // Ensure number is at least 1
        const newSessions = isNaN(num) || num < 1 ? 1 : num;
        onValuesChange({status, absent_reason, number_of_sessions: newSessions});
    }, [status, absent_reason, onValuesChange]);

    const handleStatusSelect = useCallback((selectedStatus) => {
        setTempStatus(selectedStatus);

        const config = ATTENDANCE_STATUSES.find(s => s.status === selectedStatus);

        // Preserve current reason if re-selecting the same status
        const newReason = selectedStatus === status ? absent_reason : '';

        // Immediately update the state
        onValuesChange({
            status: selectedStatus,
            number_of_sessions,
            absent_reason: config.requiresNote ? newReason : ''
        });

    }, [onValuesChange, status, absent_reason, number_of_sessions]);

    const handleReasonChange = useCallback((e) => {
        // Only update the reason field while keeping the currently selected status
        onValuesChange({status: tempStatus, number_of_sessions, absent_reason: e.target.value});
    }, [tempStatus, number_of_sessions, onValuesChange]);

    const handleClear = useCallback(() => {
        onValuesChange(null);
        setTempStatus(null);
    }, [onValuesChange]);

    const handleEditReason = useCallback(() => {
        // Force showing the reason input section
        setTempStatus(status);
    }, [status]);

    const handleBackToStatus = useCallback(() => {
        // Force showing the status selector section
        setTempStatus(null);
    }, []);

    // Logic to determine if the reason input view should be shown
    // We show it only if a status is selected, it requires a note, AND the view state (tempStatus) matches the selected status
    const shouldShowReasonInput = status !== null && currentStatusConfig?.requiresNote && (status === tempStatus);

    return (
        <div className="w-full flex flex-col gap-4">

            <AttendanceDisplay
                status={status}
                absentReason={absent_reason}
                numberOfSessions={number_of_sessions}
                currentStatusConfig={currentStatusConfig}
                onClear={handleClear}
                onEdit={handleEditReason}
            />

            <div className="p-4 bg-white rounded-xl shadow-lg border border-indigo-100">
                <h3 className="font-semibold text-gray-500 mb-3">
                    {t("number of sessions of the lesson")}
                </h3>
                <Input
                    type="number"
                    min={1}
                    value={number_of_sessions}
                    onChange={handleSessionsChange}
                    placeholder="Number of sessions"
                    className="w-full rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
            </div>

            {(status === null || !shouldShowReasonInput)
                ? (
                    <AttendanceStatusSelector
                        status={status}
                        onSelect={handleStatusSelect}
                    />
                )
                : (
                    <AttendanceReasonInput
                        absentReason={absent_reason}
                        onReasonChange={handleReasonChange}
                        tempStatusConfig={tempStatusConfig}
                        onBack={handleBackToStatus}
                    />
                )
            }
        </div>
    );
};


export default AttendanceInput
